import Vue from 'vue';
import VueIziToast from '../plugins/VueIziToast';
import iziToastOptions from './izi-toast';
import localStorageKeys from '../store/local-storage-keys';
import Auth from '@aws-amplify/auth';
import constants from '../_consts';

Vue.use(VueIziToast, {
  progressBar: false,
  position: 'topRight',
  animateInside: false,
  overlayColor: 'rgba(0, 0, 0, 0.3)',
  drag: false,
  messageColor: '#fff'
});
const defaultMethod = 'GET';

/**
 * Build api server
 */
export function buildApiServer () {
  if (parseInt(process.env.SOF_API_SERVER_HOST, 10) !== 80) {
    return `${process.env.SOF_API_SERVER_HOST}`;
  }
  return process.env.SOF_API_SERVER_HOST;
}

/**
 * Build options
 */
export function buildOptions (method, body, customHeaders, options = {}) {
  method = method || defaultMethod;
  return new Promise((resolve, reject) => {
    Auth.currentSession()
      .then((currentSession) => resolve(currentSession))
      .catch(() => resolve(null)); // user not login, not problem here, just call api without token
  }).then((currentSession) => {
    const token = currentSession && !options.skipAuthen ? `Bearer ${currentSession.idToken.jwtToken}` : null;
    localStorage.setItem(constants.LOCAL_STORE.token, token);
    return {
      method,
      headers: { ...{
        'Content-Type': 'application/json',
        'Authorization': token
      },
      ...customHeaders
      },
      data: body,
      params: body && body.params ? body.params : null
    };
  });
}

export function buildOptionsFormData (method = defaultMethod, body, customHeaders, options = {}) {
  return new Promise((resolve, reject) => {
    Auth.currentSession()
      .then((currentSession) => resolve(currentSession))
      .catch(() => resolve(null)); // user not login, not problem here, just call api without token
  }).then((currentSession) => {
    const token = currentSession && !options.skipAuthen ? `Bearer ${currentSession.idToken.jwtToken}` : null;
    localStorage.setItem(constants.LOCAL_STORE.token, token);
    return {
      method,
      headers: {
        ...customHeaders
      },
      data: body,
      params: body && body.params ? body.params : null
    };
  });
}

export function showApiCallbackErrorMessage (ex) {
  if (ex.response && ex.response.data) {
    // check if general exception not fired - status will be 500, to get error message in error list
    if (ex.response.data.status === 500) {
      if (ex.response.data.errors && ex.response.data.errors.length) {
        Vue.prototype.$toast.success(ex.response.data.errors[0].defaultMessage || 'Internal server error.', '', {
          ...iziToastOptions.optError
        });
      }
    } else {
      Vue.prototype.$toast.success((ex.response.data && ex.response.data.message) || 'Internal server error.', '', {
        ...iziToastOptions.optError
      });
    }
    return;
  }

  Vue.prototype.$toast.success('Internal server error.', '', {
    ...iziToastOptions.optError
  });
}

export function updateRequestOptions (options) {
  if (options.headers.Authorization) {
    const token = localStorage.getItem(localStorageKeys.TOKEN).replace('Bearer ', '');
    options.headers.Authorization = `Bearer ${token}`;
  }
}

/**
 * Build endpoints
 */
export function getEndPoint () {
  return {
    mfa: {
      updateMFAConfirm: `${buildApiServer()}/mfa/confirm`,
      sendNewCode: `${buildApiServer()}/mfa/non-auth/send-code`,
      confirmLogin: `${buildApiServer()}/mfa/non-auth/confirm-login`
    },
    settings: {
      updateValidator: `${buildApiServer()}/configs/update_system_config`,
      saveEmailTemplates: `${buildApiServer()}/system/update_email_config`
    },
    emailHistory: {
      resendEmail: `${buildApiServer()}/events/resend-participant-email`
    },
    clients: {
      // doLogin: buildApiServer() + '/login',
      requestResetPassword: buildApiServer() + '/public/request_reset_password',
      checkToken: buildApiServer() + '/public/check_token',
      internalPasswordChange: buildApiServer() + '/clients/password_change',
      internalPasswordReset: buildApiServer() + '/public/internal_password_reset',
      externalPasswordReset: buildApiServer() + '/public/external_password_reset',
      forceResetPassword: buildApiServer() + '/clients/force_password_reset',
      getClient: buildApiServer() + '/clients/get_client',
      getClients: buildApiServer() + '/clients',
      syncClientsAndPrograms: buildApiServer() + '/client/sync-es-clients',
      syncStaffs: buildApiServer() + '/sales-force/sync-staff-manually',
      syncEvents: buildApiServer() + '/sales-force/sync-manually',
      syncBiometricOrders: buildApiServer() + '/sales-force/sync-order-manually',
      syncUsersStatus: buildApiServer() + '/users/sync_cognito_statuses',
      syncBasicShiftBoard: buildApiServer() + '/shiftboard/sync-basic-data',
      searchClients: buildApiServer() + '/clients/search',
      importUser: buildApiServer() + '/clients/import_user',
      getAllAM: buildApiServer() + '/clients/get_all_am',
      updateClient: buildApiServer() + '/clients/update',
      verifyRecaptcha: buildApiServer() + '/public/clients/verify_recaptcha',
      updateMessage: buildApiServer() + '/clients/update_messages',
      getMessages: buildApiServer() + '/clients/get_client',
      changeNotification: buildApiServer() + '/clients/email_notification',
      updateAddOn: buildApiServer() + '/clients/update_add_on',
      updateSecondaryAM: buildApiServer() + '/clients/update_secondary_am',
      updateReseller: buildApiServer() + '/clients/update_reseller_toggle',
      updateClientManager: buildApiServer() + '/clients/update_client_manager',
      addClient: buildApiServer() + '/clients/create_new_client',
      getAllUsers: buildApiServer() + '/clients/get_users',
      updateUser: buildApiServer() + '/clients/update_user_info',
      deleteUser: buildApiServer() + '/clients/delete_user',
      createUser: buildApiServer() + '/clients/create_user',
      updateReadOnly: buildApiServer() + '/clients/update_read_only',
      updateClientName: buildApiServer() + '/clients/update_client_name',
      updateValidatorsToClient: buildApiServer() + '/clients/update_validators_to_client',
      getAllValidators: buildApiServer() + '/clients/get_all_validators',
      addValidator: buildApiServer() + '/clients/create_new_validator',
      deleteValidator: buildApiServer() + '/clients/delete_validator',
      getValidatorsByClientId: buildApiServer() + '/clients/get_validators_by_client_id',
      getValidatorsByEventId: buildApiServer() + '/clients/get_validators_by_event_id',
      getStaffLIst: buildApiServer() + '/clients/get_all_staff',
      updateStaff: buildApiServer() + '/clients/update_staff',
      deleteStaff: buildApiServer() + '/clients/delete_staff',
      getSchedulerConfiguration: buildApiServer() + '/config-scheduler/configuration',
      getAvailableEmailTags: buildApiServer() + '/config-scheduler/available-tag',
      saveConfigurations: buildApiServer() + '/config-scheduler/configuration',
      uploadAvatar: buildApiServer() + '/client/avatar/upload',
      getClientEventSchedulerStatus: buildApiServer() + '/events/client-event-scheduler-status',
      updateClientEventSchedulerStatus: buildApiServer() + '/events/client-event-scheduler-status',
      deleteClient: buildApiServer() + `/clients/delete_client`,
      getListEM: buildApiServer() + `/users/get_all_em`,
      getProgramClient: buildApiServer() + `/programs-of-client`,
      getClientsPaging: buildApiServer() + '/clients-overview'
    },
    events: {
      updateEvent: buildApiServer() + '/events/update',
      getEvents: buildApiServer() + '/events',
      countEvents: buildApiServer() + '/events/count_events',
      exportExcel: buildApiServer() + '/public/export',
      getRelatedItemInfo: buildApiServer() + '/events/count_related',
      getEventsRelated: buildApiServer() + '/events/related',
      deleteEvent: buildApiServer() + '/events/delete',
      submitEvents: buildApiServer() + '/events/submit_events',
      submitAllEvent: buildApiServer() + '/events/submit_all',
      getOnSiteContact: buildApiServer() + '/events/search_contact',
      getEventLockList: buildApiServer() + '/events/locked_events',
      removeEventLockList: buildApiServer() + '/events/remove_locked_events',
      getEventHistory: buildApiServer() + '/events/event_history',
      updateAddOn: buildApiServer() + '/events/update_add_on',
      changeEventStatus: buildApiServer() + '/events/change_status',
      getEventsByDates: buildApiServer() + '/events/get_events_by_dates',
      getDateHasData: buildApiServer() + '/events/date-has-data',
      getLockedDate: buildApiServer() + '/events/locked-date',
      getDateRangeHasData: buildApiServer() + '/events/date-range-has-data',
      getEventById: buildApiServer() + '/events/get_events_by_id',
      blockDate: buildApiServer() + '/events/block-date',
      openDate: buildApiServer() + '/events/open-date',
      blockOrOpenSlots: buildApiServer() + '/events/block-event-staffing',
      changeDuration: buildApiServer() + '/events/event-staffing-duration',
      getEventStaffing: buildApiServer() + '/events/event-staffing',
      updateEventStaffing: buildApiServer() + '/events/update-event-staffing',
      getTrackingStatus: buildApiServer() + '/events/tracking',
      getEventByStatus: buildApiServer() + '/events/get-events-by-status',
      getPackingSlip: buildApiServer() + '/public/packaging-slips/export',
      getURLForPackingSlip: buildApiServer() + '/event/packaging-slips/download-url',
      getStaffMaxSlots: buildApiServer() + '/events/set-max-staff-number',
      getAllAvailableStaffs: buildApiServer() + '/clients/get_all_available_staff',
      assignStaff: buildApiServer() + '/clients/event/assign_staff',
      getEventStaff: buildApiServer() + '/clients/get_event_staff',
      getEventReportChart: buildApiServer() + '/events/event-report-chart',
      updateIsLiveEvent: buildApiServer() + '/events/update-is-live-event',
      clearSingleSlot: buildApiServer() + '/events/slot/remove-participant',
      searchRelatedEvents: buildApiServer() + '/events/get_eligible_related_events',
      addOrRemoveRelatedEvents: buildApiServer() + '/events/add-remove-related-events',
      getDefaultEmailTemplate: buildApiServer() + '/events/get_default_templates',
      sendOnSiteContactEmail: buildApiServer() + '/events/send-onsite-contact-emails',
      getEventHistoriesById: buildApiServer() + '/events/email-histories',
      getEmailHistoryDetails: buildApiServer() + '/events/email-history-detail',
      addParticipant: buildApiServer() + '/events/add-participant-to-slot',
      updateParticipant: buildApiServer() + '/events/update-participant-to-slot',
      updateMaxNumberSlotFluShot: buildApiServer() + '/events/update-max-slot-flushot',
      searchParticipant: buildApiServer() + '/events/get-filled-slots',
      generatePackingSlip: buildApiServer() + '/public/packaging-slips/export-by-event',
      resendBiometricSurvey: `${buildApiServer()}/events/resend-biometric-survey-emails`,
      cancelAllAppointments: buildApiServer() + '/events/cancel-all-appointments',
      bulkCreateEventTemplate: buildApiServer() + '/public/event/bulk-create-event-template',
      uploadBulkCreateEvent: buildApiServer() + '/events/upload',
      uploadHistories: buildApiServer() + '/events/upload-histories',
      downloadUploadedFile: buildApiServer() + '/public/events/download-uploaded-file',
      exportParticipantReport: buildApiServer() + '/event/participant-report',
      updateEventShiftboard: buildApiServer() + '/events/{eventId}/update-shiftboard'
    },
    oauth2: {
      createUserIfNotExist: buildApiServer() + '/users/get-user-info'
    },
    staff: {
      forceResetPassword: buildApiServer() + '/staff/reset-password'
    },
    shiftboard: {
      getWorkgroups: buildApiServer() + '/shiftboard/workgroups'
    }
  };
}



// WEBPACK FOOTER //
// ./src/utils/api-call.js